import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import {
  CUSTOMER_SKINS,
  CUSTOMER_SKIN_META,
  DEFAULT_CUSTOMER_SKIN,
  useCustomerTheme,
} from '@/providers/CustomerThemeProvider';
import type { CustomerSkin } from '@/providers/CustomerThemeProvider';

/**
 * The swatch strip inside a theme card. Deliberately built from literal colour
 * values rather than the live `--customer-*` tokens: a preview must show what a
 * skin looks like WITHOUT being applied, so it cannot read the active cascade.
 * These five values per skin mirror surface / action / accent / success /
 * warning from `index.css`; they are decoration with an accessible name, never
 * a source of truth for the running theme.
 */
const SKIN_PREVIEW: Record<
  CustomerSkin,
  { bg: string; swatches: [string, string, string, string] }
> = {
  forge: { bg: '#eef1f4', swatches: ['#2c6fd1', '#0e8a66', '#e08a00', '#ffffff'] },
  aurora: { bg: '#f6f7f9', swatches: ['#1d4ed8', '#15803d', '#b45309', '#ffffff'] },
  command: { bg: '#f5f3ee', swatches: ['#0e8f8f', '#15803d', '#d97706', '#ffffff'] },
  pure: { bg: '#ffffff', swatches: ['#1d1d1f', '#248a3d', '#c77800', '#f5f5f7'] },
  horizon: { bg: '#f7f7fb', swatches: ['#4f46e5', '#047857', '#c2410c', '#ffffff'] },
  pulse: { bg: '#f3f5f7', swatches: ['#0f766e', '#15803d', '#b45309', '#ffffff'] },
};

/**
 * D-130 rule 6 — theme selection. Presentation only: choosing a skin changes
 * design tokens and nothing else. No API call, no permission, no data, no
 * route and no available action depends on it.
 *
 * A radiogroup rather than a list of buttons, so a keyboard user moves between
 * options with the arrow keys and the active option is announced as checked.
 */
export function CustomerThemePicker() {
  const { skin, setSkin } = useCustomerTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
    >
      {CUSTOMER_SKINS.map((option) => {
        const meta = CUSTOMER_SKIN_META[option];
        const preview = SKIN_PREVIEW[option];
        const active = skin === option;
        const isDefault = option === DEFAULT_CUSTOMER_SKIN;

        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => setSkin(option)}
            className={[
              'customer-pressable group relative flex w-full flex-col gap-3 overflow-hidden rounded-customer-lg border p-3 text-left transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring focus-visible:ring-offset-2 focus-visible:ring-offset-customer-bg',
              active
                ? 'border-customer-action bg-customer-surface shadow-customer'
                : 'border-customer-border bg-customer-surface hover:border-customer-action/40',
            ].join(' ')}
          >
            {/* Preview plate. aria-hidden: the swatches carry no information the
                name and description do not already give, so a screen reader
                should not read six colour chips per card. */}
            <span
              aria-hidden="true"
              className="flex h-16 w-full items-end gap-1.5 rounded-customer border border-customer-border/70 p-2"
              style={{ backgroundColor: preview.bg }}
            >
              <span
                className="h-full w-8 shrink-0 rounded-[4px]"
                style={{ backgroundColor: preview.swatches[3] }}
              />
              <span className="flex flex-1 flex-col justify-end gap-1">
                <span
                  className="h-1.5 w-full rounded-full"
                  style={{ backgroundColor: preview.swatches[0], opacity: 0.35 }}
                />
                <span className="flex items-center gap-1">
                  <span
                    className="h-4 flex-1 rounded-[4px]"
                    style={{ backgroundColor: preview.swatches[0] }}
                  />
                  <span
                    className="h-4 w-4 shrink-0 rounded-[4px]"
                    style={{ backgroundColor: preview.swatches[1] }}
                  />
                  <span
                    className="h-4 w-4 shrink-0 rounded-[4px]"
                    style={{ backgroundColor: preview.swatches[2] }}
                  />
                </span>
              </span>
            </span>

            <span className="flex min-w-0 items-start gap-2">
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-sm font-semibold text-customer-text">{meta.name}</span>
                  {isDefault && (
                    <span className="rounded-full bg-customer-surface-raised px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-customer-text-secondary">
                      Default
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block break-words text-xs leading-snug text-customer-text-secondary">
                  {meta.description}
                </span>
              </span>
              {/* Not colour alone: the active card also carries a check icon and
                  aria-checked, so the state survives a monochrome rendering. */}
              <span
                aria-hidden="true"
                className={[
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors',
                  active
                    ? 'border-customer-action bg-customer-action text-customer-action-ink'
                    : 'border-customer-border text-transparent',
                ].join(' ')}
              >
                <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5" />
              </span>
            </span>
            {active && <span className="sr-only">Currently applied</span>}
          </button>
        );
      })}
    </div>
  );
}
